import { useEffect, useState, useRef } from "react";
import { EXT_GET_MONTHLY_EXTENSION_REPORT, EXT_GET_MONTHLY_EXTENSION_REPORT_FILTERS, COUNTRY_CODE_MAP } from "../../../../constants/constant";
import { getDataWAuth } from "../../../../utils/api-service";
import { useSelector } from "react-redux";
import { getUserCountry } from "../../../../utils/utils";

export const useMonthlyExtensionReport = () => {
  const [rawData, setRawData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [viewMode, setViewMode] = useState('chart'); // 'table' or 'chart' - default to chart
  const [isLoading, setIsLoading] = useState(true); // Start with loading true for initial data fetch
  const [hasNoData, setHasNoData] = useState(false);
  const hasInitialized = useRef(false);
  const userData = useSelector((state) => state.userState.userData);

  // Helper function to sort countries by their display names
  const sortCountriesByName = (countries) => {
    return countries.sort((a, b) => {
      const nameA = COUNTRY_CODE_MAP[a] || a;
      const nameB = COUNTRY_CODE_MAP[b] || b;
      return nameA.localeCompare(nameB);
    });
  };
  
  // Filter options from API
  const [extensionTypeOptions, setExtensionTypeOptions] = useState([]);
  const [materialTypeOptions, setMaterialTypeOptions] = useState([]);
  const [requestStatusOptions, setRequestStatusOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [currency, setCurrency] = useState('');
  
  // Separate state for applied filters (only updated when Apply button is clicked)
  const [appliedFilters, setAppliedFilters] = useState(null);
  
  // State to track initial filters after preselection (for comparison to enable/disable Apply button)
  const [initialFilters, setInitialFilters] = useState(null);
  
  // Track if user has ever manually changed any filter (to allow re-applying initial values after changes)
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    extensionTypes: [],
    volume: 'count',
    startMonth: null,
    endMonth: null,
    startYear: null,
    endYear: null,
    materialTypes: [],
    requestStatus: [],
    country: []
  });

  // Static options for volume
  const volumeOptions = [
    { value: 'count', label: 'Count' },
    { value: 'value', label: 'Value' }
  ];

  // Month options
  const monthOptions = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' }
  ];

  // Year options (current year and years back to 2023)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let year = currentYear; year >= 2023; year--) {
    yearOptions.push(year);
  }

  const handleFilterChange = (filterName, value) => {
    // Mark that user has manually interacted with filters
    setHasUserInteracted(true);
    
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleMultiSelectChange = (filterName, value) => {
    // Mark that user has manually interacted with filters
    setHasUserInteracted(true);
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterName]: typeof value === 'string' ? value.split(',') : value
      };
      
      // If extensionTypes is being changed and IE is being deselected, clear materialTypes
      if (filterName === 'extensionTypes') {
        const newExtensionTypes = typeof value === 'string' ? value.split(',') : value;
        if (!newExtensionTypes.includes('IE')) {
          newFilters.materialTypes = [];
        }
      }
      
      return newFilters;
    });
  };

  // Check if all required filters are filled
  const areAllFiltersValid = () => {
    return (
      // Extension types must be selected (at least one)
      filters.extensionTypes && filters.extensionTypes.length > 0 &&
      
      // Volume must be selected (should always be selected, but check anyway)
      filters.volume &&
      
      // Date range must be complete
      filters.startMonth && 
      filters.endMonth && 
      filters.startYear && 
      filters.endYear &&
      
      // Material types must be selected (at least one) if IE is selected
      (!filters.extensionTypes.includes('IE') || (filters.materialTypes && filters.materialTypes.length > 0)) &&
      
      // Request status must be selected (at least one)
      filters.requestStatus && filters.requestStatus.length > 0 &&
      
      // Country must be selected
      filters.country
    );
  };

  // Function to check if current filters are different from initial filters
  const haveFiltersChanged = () => {
    // If user has never interacted manually, only allow Apply if filters differ from initial
    if (!hasUserInteracted) {
      if (!initialFilters) {
        return false;
      }
      
      // Compare each filter field against initial
      const arraysEqual = (a, b) => {
        if (a.length !== b.length) return false;
        return a.every((val, index) => val === b[index]);
      };
      
      const extensionTypesChanged = !arraysEqual(filters.extensionTypes.sort(), initialFilters.extensionTypes.sort());
      const volumeChanged = filters.volume !== initialFilters.volume;
      const startMonthChanged = filters.startMonth !== initialFilters.startMonth;
      const endMonthChanged = filters.endMonth !== initialFilters.endMonth;
      const startYearChanged = filters.startYear !== initialFilters.startYear;
      const endYearChanged = filters.endYear !== initialFilters.endYear;
      const materialTypesChanged = !arraysEqual(filters.materialTypes.sort(), initialFilters.materialTypes.sort());
      const requestStatusChanged = !arraysEqual(filters.requestStatus.sort(), initialFilters.requestStatus.sort());
      const countryChanged = filters.country !== initialFilters.country;
      
      const hasChanged = extensionTypesChanged || volumeChanged || startMonthChanged || 
        endMonthChanged || startYearChanged || endYearChanged || materialTypesChanged || 
        requestStatusChanged || countryChanged;
      
      return hasChanged;
    }
    
    // If user has interacted, compare against applied filters (or allow if no applied filters yet)
    if (!appliedFilters) {
      return true;
    }
    
    // Compare each filter field against applied
    const arraysEqual = (a, b) => {
      if (a.length !== b.length) return false;
      return a.every((val, index) => val === b[index]);
    };
    
    const extensionTypesChanged = !arraysEqual(filters.extensionTypes.sort(), appliedFilters.extensionTypes.sort());
    const volumeChanged = filters.volume !== appliedFilters.volume;
    const startMonthChanged = filters.startMonth !== appliedFilters.startMonth;
    const endMonthChanged = filters.endMonth !== appliedFilters.endMonth;
    const startYearChanged = filters.startYear !== appliedFilters.startYear;
    const endYearChanged = filters.endYear !== appliedFilters.endYear;
    const materialTypesChanged = !arraysEqual(filters.materialTypes.sort(), appliedFilters.materialTypes.sort());
    const requestStatusChanged = !arraysEqual(filters.requestStatus.sort(), appliedFilters.requestStatus.sort());
    const countryChanged = filters.country !== appliedFilters.country;
    
    const hasChanged = extensionTypesChanged || volumeChanged || startMonthChanged || 
      endMonthChanged || startYearChanged || endYearChanged || materialTypesChanged || 
      requestStatusChanged || countryChanged;
    
    return hasChanged;
  };

  // Function to check if Apply button should be enabled
  const isApplyButtonEnabled = () => {
    const allValid = areAllFiltersValid();
    const hasChanged = haveFiltersChanged();
    
    return allValid && hasChanged;
  };

  const applyFilters = () => {
    // Save current filters as applied filters
    setAppliedFilters({ ...filters });
    
    // Set loading state
    setIsLoading(true);
    
    // Build filter parameters for backend request
    const filterParams = {
      extensionTypes: filters.extensionTypes.length > 0 ? filters.extensionTypes : undefined,
      materialTypes: filters.extensionTypes.includes('IE') 
        ? (filters.materialTypes.length > 0 ? filters.materialTypes : [])
        : [],
      requestStatus: filters.requestStatus.length > 0 ? filters.requestStatus : undefined,
      country: filters.country ? filters.country : undefined,
      startMonth: filters.startMonth,
      endMonth: filters.endMonth,
      startYear: filters.startYear,
      endYear: filters.endYear,
      volume: filters.volume
    };
    
    // Remove undefined values from the parameters
    const cleanedParams = Object.fromEntries(
      Object.entries(filterParams).filter(([_, value]) => value !== undefined)
    );
    
    // Fetch filtered data from backend
    fetchMonthlyExtensionReport(cleanedParams, false);
    
    return true; // Return success indicator for closing filter panel
  };

  const clearFilters = () => {
    // Reset filters to initial state
    setFilters({
      extensionTypes: [],
      volume: 'count',
      startMonth: null,
      endMonth: null,
      startYear: null,
      endYear: null,
      materialTypes: [],
      requestStatus: [],
      country: ''
    });
    // Also reset applied filters to null (this will show initial data without making new API call)
    setAppliedFilters(null);
    // Reset initial filters to null so Apply button will be enabled once user makes changes
    setInitialFilters(null);
    // Note: Don't fetch data here, let the initial data remain displayed
    // The user can apply filters again if they want to refresh
  };

  const fetchMonthlyExtensionReport = async (filterParams = null, isInitialLoad = false) => {
    try {
      
      // Set loading for both initial load and filter applications
      setIsLoading(true);
      
      let apiUrl = EXT_GET_MONTHLY_EXTENSION_REPORT;
      
      // If filter parameters are provided, use the filtered endpoint
      if (filterParams && Object.keys(filterParams).length > 0) {
        const queryParams = new URLSearchParams();
        
        // Add filter parameters if they exist
        if (filterParams.extensionTypes && filterParams.extensionTypes.length > 0) {
          queryParams.append('extensionType', filterParams.extensionTypes.join(','));
        }
        // Always send materialTypes when extensionTypes includes IE or when IE is not selected (empty array)
        if (filterParams.materialTypes !== undefined) {
          queryParams.append('materialType', filterParams.materialTypes.join(','));
        }
        if (filterParams.requestStatus && filterParams.requestStatus.length > 0) {
          queryParams.append('requestStatus', filterParams.requestStatus.join(','));
        }
        if (filterParams.country) {
          queryParams.append('country', filterParams.country);
        }
        if (filterParams.startMonth) {
          queryParams.append('startMonth', filterParams.startMonth);
        }
        if (filterParams.endMonth) {
          queryParams.append('endMonth', filterParams.endMonth);
        }
        if (filterParams.startYear) {
          queryParams.append('startYear', filterParams.startYear);
        }
        if (filterParams.endYear) {
          queryParams.append('endYear', filterParams.endYear);
        }
    
        // Build the filtered API URL with query parameters
        apiUrl = queryParams.toString() 
          ? `${EXT_GET_MONTHLY_EXTENSION_REPORT_FILTERS}?${queryParams.toString()}`
          : EXT_GET_MONTHLY_EXTENSION_REPORT_FILTERS;
      }
      
      const response = await getDataWAuth(apiUrl);
      if (response.status === 200) {
        const data = response.data;
        
        // Check if extensionRequestList exists and has data
        if (data.extensionRequestList && data.extensionRequestList.length > 0) {
          setRawData(data.extensionRequestList);
          setHasNoData(false);
          
          // Extract currency from the first record that has currency information
          const firstRecord = data.extensionRequestList[0];
          // Use currencyKey field from the API response
          const currencyValue = firstRecord.currencyKey || 
                               firstRecord.currency || 
                               firstRecord.currencyCode || 
                               'USD'; // fallback to USD if no currency found
          setCurrency(currencyValue);
        } else {
          // No data available
          setRawData([]);
          setHasNoData(true);
          setCurrency('USD'); // Set default currency when no data
        }
        
        // Process filter options even when there's no data, as long as field lists are provided
        if (data) {
          // Extract unique extension types from the data
          if (data.extensionTypeList || (data.extensionRequestList && data.extensionRequestList.length > 0)) {
            let extensionTypes = data.extensionTypeList || 
              [...new Set(data.extensionRequestList?.map(item => item.extensionType).filter(Boolean))];
            
            // Special handling for EF extension type
            if (extensionTypes.includes('EF')) {
              extensionTypes = extensionTypes.filter(type => type !== 'EF');
              
              const combinedOptions = [];
              if (!extensionTypes.includes('EF+CF')) {
                combinedOptions.push('EF+CF');
              }
              if (!extensionTypes.includes('EF+CL')) {
                combinedOptions.push('EF+CL');
              }
              
              extensionTypes = [...combinedOptions, ...extensionTypes];
            }
            
            // Custom sorting function for extension types: IE → EF+CF → EF+CL → TI → LO → LD → TD → RR → AP
            const sortExtensionTypes = (types) => {
              const priorityOrder = ['IE', 'EF+CF', 'EF+CL', 'TI', 'LO', 'LD', 'TD', 'RR', 'AP'];
              
              return types.sort((a, b) => {
                const indexA = priorityOrder.indexOf(a);
                const indexB = priorityOrder.indexOf(b);
                
                // If both are in priority list, sort by priority order
                if (indexA !== -1 && indexB !== -1) {
                  return indexA - indexB;
                }
                // If only a is in priority list, a comes first
                if (indexA !== -1 && indexB === -1) {
                  return -1;
                }
                // If only b is in priority list, b comes first
                if (indexA === -1 && indexB !== -1) {
                  return 1;
                }
                // If neither is in priority list, sort alphabetically
                return a.localeCompare(b);
              });
            };
            
            const sortedExtensionTypes = sortExtensionTypes(extensionTypes);
            
            // Preserve user's selected extension types even if they don't appear in new data
            const currentSelectedExtensionTypes = filters.extensionTypes || [];
            const mergedExtensionTypes = [...new Set([...sortedExtensionTypes, ...currentSelectedExtensionTypes])];
            const finalSortedExtensionTypes = sortExtensionTypes(mergedExtensionTypes);
            
            setExtensionTypeOptions(finalSortedExtensionTypes);
            
            // Only preselect extension types on initial load
            if (isInitialLoad) {
              // Preselect only these 4 extension types (IE, EF+CF, EF+CL, TI) on initial load
              const allExtensionTypes = ['IE', 'EF+CF', 'EF+CL', 'TI'];
              
              setFilters(prev => ({
                ...prev,
                extensionTypes: allExtensionTypes
              }));
            }
          }
          
          // Extract unique material types from the data
          if (data.materialTypeList || (data.extensionRequestList && data.extensionRequestList.length > 0)) {
            const materialTypes = data.materialTypeList || 
              [...new Set(data.extensionRequestList?.map(item => item.productType).filter(Boolean))];
            const sortedMaterialTypes = materialTypes.sort();
            
            // Preserve user's selected material types even if they don't appear in new data
            const currentSelectedMaterialTypes = filters.materialTypes || [];
            const mergedMaterialTypes = [...new Set([...sortedMaterialTypes, ...currentSelectedMaterialTypes])];
            const finalSortedMaterialTypes = mergedMaterialTypes.sort();
            
            setMaterialTypeOptions(finalSortedMaterialTypes);
            
            // Only preselect material types on initial load
            if (isInitialLoad) {
              // Preselect ALL available material types on initial load
              setFilters(prev => ({
                ...prev,
                materialTypes: sortedMaterialTypes
              }));
            }
          }
          
          // Extract unique request status from the data
          if (data.statusList || (data.extensionRequestList && data.extensionRequestList.length > 0)) {
            const requestStatus = data.statusList || 
              [...new Set(data.extensionRequestList?.map(item => item.requestStatus).filter(Boolean))];
            const sortedRequestStatus = requestStatus.sort();
            
            // Preserve user's selected request statuses even if they don't appear in new data
            const currentSelectedRequestStatus = filters.requestStatus || [];
            const mergedRequestStatus = [...new Set([...sortedRequestStatus, ...currentSelectedRequestStatus])];
            const finalSortedRequestStatus = mergedRequestStatus.sort();
            
            setRequestStatusOptions(finalSortedRequestStatus);
            
            // Only preselect request statuses on initial load
            if (isInitialLoad) {
              // Preselect ALL available request statuses on initial load
              setFilters(prev => ({
                ...prev,
                requestStatus: sortedRequestStatus
              }));
            }
          }
          
          // Extract unique countries from the data
          
          if (data.country && Array.isArray(data.country) && data.country.length > 0) {
            // Use data.country for dropdown options (all available countries)
            // data.country is an array of strings, not objects with countryCode
            const countries = data.country.filter(Boolean);
            const sortedCountries = sortCountriesByName(countries);
            setCountryOptions(sortedCountries);
            
            // Preselect country based on actual data for both initial load AND filter applications
            if (data.extensionRequestList && data.extensionRequestList.length > 0) {
              const firstCountry = data.extensionRequestList.find(item => item.country)?.country;
              
              // Preselect the country that exists in actual data and is available in country options
              if (firstCountry && sortedCountries.includes(firstCountry)) {
                setFilters(prev => ({
                  ...prev,
                  country: firstCountry
                }));
              }
            } else {
              // If no data in extensionRequestList, assign country based on user's country
              const userCountry = getUserCountry(userData);
              if (userCountry && sortedCountries.includes(userCountry)) {
                setFilters(prev => ({
                  ...prev,
                  country: userCountry
                }));
              } else if (userData?.country && sortedCountries.includes(userData.country)) {
                // Fallback: use userData.country directly if getUserCountry doesn't work
                setFilters(prev => ({
                  ...prev,
                  country: userData.country
                }));
              }
            }
          } else if (data.extensionRequestList && data.extensionRequestList.length > 0) {
            // Fallback: if data.country is not available, use countries from extensionRequestList
            const firstCountry = data.extensionRequestList.find(item => item.country)?.country;
            if (firstCountry) {
              const uniqueCountries = [...new Set(data.extensionRequestList.map(item => item.country).filter(Boolean))];
              const sortedCountries = sortCountriesByName(uniqueCountries);
              setCountryOptions(sortedCountries);
              setFilters(prev => ({
                ...prev,
                country: firstCountry
              }));
            }
          } else {
            // If no country data from API, try to use user's country if available in options
            const userCountry = getUserCountry(userData);
            if (userCountry) {
              setFilters(prev => ({
                ...prev,
                country: userCountry
              }));
            } else if (userData?.country) {
              setFilters(prev => ({
                ...prev,
                country: userData.country
              }));
            }
          }
          
          // Pre-select date range (last 6 months) if not already set
          if (isInitialLoad) {
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
            
            // Calculate 6 months ago
            let startMonth = currentMonth - 5;
            let startYear = currentYear;
            if (startMonth <= 0) {
              startMonth += 12;
              startYear -= 1;
            }
            
            setFilters(prev => ({
              ...prev,
              startMonth: startMonth,
              endMonth: currentMonth,
              startYear: startYear,
              endYear: currentYear
            }));
            
            // Also set appliedFilters for initial load so the country displays immediately
            // Use setTimeout to ensure filters state is updated first
            setTimeout(() => {
              setAppliedFilters(currentFilters => {
                // Get the current filters state (including the country that was just set)
                const updatedFilters = {
                  ...filters,
                  startMonth: startMonth,
                  endMonth: currentMonth,
                  startYear: startYear,
                  endYear: currentYear
                };
                
                // Find the country from the current data processing
                let countryToApply = '';
                if (data.country && Array.isArray(data.country) && data.country.length > 0) {
                  if (data.extensionRequestList && data.extensionRequestList.length > 0) {
                    countryToApply = data.extensionRequestList.find(item => item.country)?.country || '';
                  } else {
                    // Use user's country if no data
                    const userCountry = getUserCountry(userData);
                    if (userCountry && data.country.includes(userCountry)) {
                      countryToApply = userCountry;
                    } else if (userData?.country && data.country.includes(userData.country)) {
                      countryToApply = userData.country;
                    }
                  }
                } else if (data.extensionRequestList && data.extensionRequestList.length > 0) {
                  countryToApply = data.extensionRequestList.find(item => item.country)?.country || '';
                } else {
                  // Fallback to user country
                  const userCountry = getUserCountry(userData);
                  countryToApply = userCountry || userData?.country || '';
                }
                
                return {
                  ...updatedFilters,
                  country: countryToApply
                };
              });
            }, 0);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching monthly extension report:", error);
    } finally {
      // Reset loading state
      setIsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchMonthlyExtensionReport(null, true);
    } else {
      console.log('Already initialized, skipping API call');
    }
  }, []); // Empty dependency array to run only once

  // Capture initial filters after preselection is complete
  useEffect(() => {
    // Only capture initial filters if:
    // 1. We don't have initial filters set yet
    // 2. All required filter options are loaded
    // 3. Filters have been preselected (not empty)
    if (
      !initialFilters && 
      extensionTypeOptions.length > 0 && 
      materialTypeOptions.length > 0 && 
      requestStatusOptions.length > 0 &&
      filters.extensionTypes.length > 0 && 
      filters.materialTypes.length > 0 && 
      filters.requestStatus.length > 0 &&
      filters.startMonth && 
      filters.endMonth && 
      filters.startYear && 
      filters.endYear &&
      filters.country
    ) {
      setInitialFilters({ ...filters });
    }
  }, [
    initialFilters,
    extensionTypeOptions.length,
    materialTypeOptions.length, 
    requestStatusOptions.length,
    filters.extensionTypes.length,
    filters.materialTypes.length,
    filters.requestStatus.length,
    filters.startMonth,
    filters.endMonth,
    filters.startYear,
    filters.endYear,
    filters.country
  ]);

  return {
    // State
    rawData,
    reportData,
    columns,
    viewMode,
    filters,
    appliedFilters,
    currency,
    isLoading,
    hasNoData,
    
    // Options
    extensionTypeOptions,
    materialTypeOptions,
    requestStatusOptions,
    countryOptions,
    volumeOptions,
    monthOptions,
    yearOptions,
    
    // State setters
    setViewMode,
    setReportData,
    setColumns,
    
    // Handlers
    handleFilterChange,
    handleMultiSelectChange,
    applyFilters,
    clearFilters,
    areAllFiltersValid,
    isApplyButtonEnabled,
    
    // User context
    userData
  };
};